/* eslint-disable */
import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useState, useCallback, useEffect } from 'react';

import StationLocator from '../../../assets/icons/dashboard/station-locator.svg';
import { useGoogleMaps } from '../../../contexts/google-map-context/GoogleMapContent';
import BatteryLoader from '../../battery-loader/BatteryLoader';
import MyButton from '../../button/Button';

interface SelectLatLongProperties {
  latitude: string | number | null;
  longitude: string | number | null;
  handleLatLongChange: (latitude: number, longitude: number, address?: string, geoLocation?: any[]) => void;
}

const containerStyle = { width: '800px', height: '450px' }; // Map container style

/**
 * Select location entire popup with buttons
 * @param {any} param0 - The param0
 * @param {any} param0.latitude - The latitude prop
 * @param {any} param0.longitude - The longitude prop
 * @param {any} param0.handleLatLongChange - The handleLatLongChange prop
 */
function SelectLatLong({ latitude, longitude, handleLatLongChange }: SelectLatLongProperties) {
  const { isLoaded } = useGoogleMaps();

  const [selectedLocation, setSelectedLocation] = useState<{ lat: number, lng: number, geoLocation: any[]}>({
    lat: Number(latitude),
    lng: Number(longitude),
    geoLocation: [],
  });
  const [selectedAddress, setSelectedAddress] = useState<string>('');

  // Default center for the map
  const [mapCenter, setMapCenter] = useState({
    lat: 41.8781, // Example: San Francisco
    lng: -87.6298,
  });

  /**
   * Get the Geo Location details
   */
  const getGeoLocation = (place: any) => {
    // Country-specific mapping
    const levelMapping: Record<string, Record<string, { name: string; key: string }>> = {
      // India
      IN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_3: { name: 'Taluk', key: 'taluk' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'State', key: 'state' },
        country: { name: 'Country', key: 'country' },
      },

      // Saudi Arabia
      SA: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Governorate', key: 'governorate' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // United Arab Emirates
      AE: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_1: { name: 'Emirate', key: 'emirate' },
        country: { name: 'Country', key: 'country' },
      },

      // Bahrain
      BH: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Kuwait
      KW: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Ethiopia
      ET: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Zone', key: 'zone' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // China
      CN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Prefecture', key: 'prefecture' },
        administrative_area_level_1: { name: 'Province', key: 'province' },
        country: { name: 'Country', key: 'country' },
      },
    };

    // Get country code first
    let countryCode = '';

    place.address_components?.forEach(({ short_name, types }: any) => {
      if (types.includes('country')) {
        countryCode = short_name;
      }
    });

    const mapping = levelMapping[countryCode] || {
      locality: { name: 'City', key: 'city' },
      administrative_area_level_4: {
        name: 'Administrative Area Level 4',
        key: 'level4',
      },
      administrative_area_level_3: {
        name: 'Administrative Area Level 3',
        key: 'level3',
      },
      administrative_area_level_2: {
        name: 'Administrative Area Level 2',
        key: 'level2',
      },
      administrative_area_level_1: {
        name: 'Administrative Area Level 1',
        key: 'level1',
      },
      country: { name: 'Country', key: 'country' },
    };

    const result: Array<{
      name: string;
      key: string;
      value: string;
    }> = [];

    place.address_components?.forEach(({ long_name, types }: any) => {
      const type = types.find((t: any) => mapping[t as keyof typeof mapping]);

      if (type) {
        const { name, key } = mapping[type];

        result.push({
          name,
          key,
          value: long_name,
        });
      }
    });
    return result;
  };

  // Handle map click event to update the selected location
  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = parseFloat(event.latLng.lat().toFixed(5));
      const lng = parseFloat(event.latLng.lng().toFixed(5));
      const geocoder = new google.maps.Geocoder();      

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          setSelectedLocation({ lat, lng, geoLocation: getGeoLocation(results[0]) });
          setSelectedAddress(results[0].formatted_address);
        } else {
          setSelectedAddress('');
        }
      });
    }
  }, []);

  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter({
        lat: Number(latitude),
        lng: Number(longitude),
      });
    }
  }, []);

  if (!isLoaded)
    return (
      <div>
        <BatteryLoader />
      </div>
    );

  return (
    <div className="select-latLong-wrap">
      <div className="select-latLong-container">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={17}
          onClick={onMapClick}>
          <Marker
            icon={{
              url: StationLocator,
              scaledSize: new window.google.maps.Size(44, 44), // Width, Height
            }}
            position={selectedLocation}
          />
        </GoogleMap>
      </div>
      <div className="select-latLong-buttons">
        <MyButton
          label="Submit"
          buttonType="submit"
          onClickFunc={() =>
            handleLatLongChange(
              Number(selectedLocation.lat),
              Number(selectedLocation.lng),
              selectedAddress,
              selectedLocation.geoLocation,
            )
          }
        />
      </div>
    </div>
  );
}

export default SelectLatLong;
